/**
 * File: ContextDocuments.tsx
 * Description: Component for displaying and managing both uploaded and selected context documents
 * Author: GitHub Copilot
 * Created: 2025-09-10
 * Last Modified: 2025-09-10
 */

import {
  DocumentTextIcon,
  MagnifyingGlassIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  EyeIcon,
  TrashIcon,
  LinkIcon,
  CalendarIcon,
  UserIcon,
  TagIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import { humanizeDescription } from '../../utils/humanizeDescription';
import { ExistingDocument } from '../ContextBrowser/ContextBrowser';
import { UploadedFile } from '../FileUploader/FileUploader';

interface ContextDocumentsProps {
  uploadedFiles: UploadedFile[];
  selectedSearchDocuments: ExistingDocument[];
  onFileRemove: (fileId: string) => void;
  onSearchDocumentRemove: (documentId: string) => void;
  onDocumentPreview: (document: ExistingDocument | UploadedFile) => void;
}

/**
 * Convert UploadedFile to ExistingDocument format for consistent handling
 */
const convertUploadedFileToDocument = (file: UploadedFile): ExistingDocument => {
  let lastModified: Date;
  if (file.uploadDate && file.uploadDate !== '') {
    lastModified = new Date(file.uploadDate);
  } else if (file.file.lastModified) {
    lastModified = new Date(file.file.lastModified);
  } else {
    lastModified = new Date();
  }
  
  
  return {
    id: file.id,
    name: file.name,
    type: 'Other',
    category: 'Uploaded Document',
    size: file.size,
    lastModified,
    author: 'Current User',
    tags: [],
    path: file.metadata?.blob_path as string || '',
    description: `Uploaded ${file.type}`,
    // Store blob path and metadata for API access
    blobPath: file.metadata?.blob_path as string,
    metadata: file.metadata,
  };
};

/**
 * Format file size for display
 */
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {return '0 B';}
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
};

/**
 * Format date for display
 */
const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};

/**
 * Get status badge styling based on upload status
 */
const getStatusBadge = (status: UploadedFile['status']) => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'uploading':
      return 'bg-yellow-100 text-yellow-800';
    case 'error':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

/**
 * Get status text based on upload status
 */
const getStatusText = (status: UploadedFile['status']) => {
  switch (status) {
    case 'completed':
      return 'Uploaded';
    case 'uploading':
      return 'Uploading...';
    case 'error':
      return 'Failed';
    default:
      return 'Unknown';
  }
};

export const ContextDocuments: React.FC<ContextDocumentsProps> = ({
  uploadedFiles,
  selectedSearchDocuments,
  onFileRemove,
  onSearchDocumentRemove,
  onDocumentPreview,
}) => {
  const totalDocuments = uploadedFiles.length + selectedSearchDocuments.length;

  if (totalDocuments === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-4">
          <DocumentTextIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900">Context Documents</h3>
          <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
            {totalDocuments}
          </span>
        </div>
        <div className="text-center py-8 text-gray-500">
          <DocumentTextIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">No context documents added yet.</p>
          <p className="text-xs mt-1">Upload files or select documents from the knowledge base to get started.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <DocumentTextIcon className="w-5 h-5 text-gray-400" />
        <h3 className="text-lg font-semibold text-gray-900">Context Documents</h3>
        <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
          {totalDocuments}
        </span>
      </div>

      <div className="space-y-4">
        {/* Uploaded Files Section */}
        {uploadedFiles.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <CloudArrowUpIcon className="w-4 h-4 text-green-600" />
              <h4 className="text-sm font-medium text-gray-700">Uploaded Documents</h4>
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                {uploadedFiles.length}
              </span>
            </div>
            <div className="space-y-2">
              {uploadedFiles.map((file) => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {file.name}
                        </p>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusBadge(file.status)}`}>
                          {getStatusText(file.status)}
                        </span>
                        {file.progress !== undefined && file.status === 'uploading' && (
                          <span className="text-xs text-gray-500">{file.progress}%</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>Uploaded Document</span>
                      </div>
                      {file.progress !== undefined && file.status === 'uploading' && (
                        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-2">
                          <div 
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300" 
                            style={{ width: `${file.progress}%` }}
                          ></div>
                        </div>
                      )}
                      {file.error && (
                        <p className="text-xs text-red-600 mt-1">{file.error}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <button
                      onClick={() => onDocumentPreview(convertUploadedFileToDocument(file))}
                      className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200 transition-colors"
                      title="Preview document"
                    >
                      <EyeIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onFileRemove(file.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                      title="Remove document"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Selected Search Documents Section */}
        {selectedSearchDocuments.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <MagnifyingGlassIcon className="w-4 h-4 text-blue-600" />
              <h4 className="text-sm font-medium text-gray-700">Knowledge Base Documents</h4>
              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                {selectedSearchDocuments.length}
              </span>
            </div>
            <div className="space-y-2">
              {selectedSearchDocuments.map((document) => (
                <div
                  key={document.id}
                  className="p-3 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <DocumentTextIcon className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {document.name}
                        </p>
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Knowledge Base
                        </span>
                      </div>
                      {humanizeDescription(
                        document.summary ?? document.description,
                      ) && (
                        <p className="text-xs text-gray-600 mt-1 line-clamp-2 mb-2">
                          {humanizeDescription(
                            document.summary ?? document.description,
                          )}
                        </p>
                      )}
                      
                      {/* Document Metadata */}
                      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-3 h-3" />
                          <span>{formatDate(document.lastModified)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <UserIcon className="w-3 h-3" />
                          <span>{document.author}</span>
                        </div>

                        {/* Wiki Link with Icon - Bottom Right */}
                        {document.source === 'wiki' && document.wiki_path && (
                          <a
                            href={document.wiki_path}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                          >
                            <LinkIcon className="w-3 h-3" />
                            Wiki Link
                          </a>
                        )}
                      </div>

                      {/* Tags */}
                      {document.tags.length > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <TagIcon className="w-3 h-3 text-gray-400" />
                          <div className="flex flex-wrap gap-1">
                            {document.tags.slice(0, 3).map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded"
                              >
                                {tag}
                              </span>
                            ))}
                            {document.tags.length > 3 && (
                              <span className="text-xs text-gray-500">
                                +{document.tags.length - 3} more
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                      <button
                        onClick={() => onSearchDocumentRemove(document.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
                        title="Remove from context"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
